import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { CoreService } from '../../services'
import {
  Category,
  Currency,
  Depot,
  File,
  Producer,
  Product,
  ProductDepot,
  ProductDepotMetric,
  Supplier,
  Tag,
} from 'depoto-core/src/entities'
import { SchemaUtil, getThumbUrl as imageGetThumbUrl } from '../../utils'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import * as Sentry from '@sentry/angular-ivy'
import { DOCUMENT } from '@angular/common'
import { ScrollService } from '../../services/scroll.service'
import { CompanyUtilService } from '../../services/company-util.service'

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  providers: [ScrollService],
})
export class ProductsComponent implements OnInit, OnDestroy, CheckDataChanges {
  depots: Depot[] = []
  products: Product[] = []
  suppliers: Supplier[] = []
  producers: Producer[] = []
  tags: Tag[] = []
  listTypes: { val: string; name: string }[] = [
    { val: 'parents', name: 'Hlavní produkty' },
    { val: 'parents_with_children', name: 'Hlavní s variantami' },
    { val: 'parents_without_children', name: 'Hlavní bez variant' },
    { val: 'children_and_parents_without_children', name: 'Skladové položky' },
    { val: 'children', name: 'Varianty' },
    { val: 'all', name: 'Vše' },
  ]
  availabilityTypes: string[] = ['all', 'stock', 'unavailable']
  automaticOrderingTypes: string[] = ['all', 'yes', 'no']
  enabledTypes: string[] = ['all', 'yes', 'no']
  metricsTypes: string[] = ['', 'less_than_minimum', 'less_than_optimum', 'more_than_optimum']
  selectedAvailability = 'all'
  expirations: string[] = ['all', 'monthEnd', 'after1month', 'after3months', 'custom']
  selectedExpiration = 'all'
  selectedExpirationDate: string
  automaticOrderingType = 'all'
  totalItems = 0
  currentPage: number
  endPage: number
  filterVisible = false
  filterListType = 'children_and_parents_without_children'
  filterEnabled = 'yes'
  filterMetrics = ''
  filterFulltext = ''
  filterCategories: Category[] = []
  categories: Category[] = []
  filterDepots: number[] = []
  filterProducers: number[] = []
  filterSuppliers: number[] = []
  filterTags: number[] = []
  filterName = ''
  filterCode = ''
  filterEan = ''
  sortProp = 'name'
  sortDir = 'asc'
  canCreate = false
  loading = true
  filterRowsShown = false
  keyEventSubscriptions: any = {}
  checkedProducts: Product[] = []
  areAllProductsChecked = false
  reloading = true
  rolledStockRowes = []
  areAllStockRowsVisible = false
  isEditingMetrics = false
  productDepotMetrics: ProductDepotMetric[] = []

  productsWithStockAndMetrics = []

  productDepotsOfOneProduct = []
  productDepotMetricsOfOneProduct = []

  endDate
  hasRoleProductUpdate: boolean
  hasRoleMetricsUpdate: boolean
  hasRoleMetricsCreate: boolean
  hasRoleExportProductsForMetrics: boolean
  purchaseCurrencyFromCompany: Currency // there is no way to change this across hs, so it's certain we can use this

  metricsLoading = false
  arePositionsEnabled: boolean
  selectedColumns: any[] = [
    { action: 'name', label: 'product.name', isDefault: true, isSelected: true, colSpan: 3 },
    { action: 'mCode', label: 'm.code', isDefault: false, isSelected: true, colSpan: 1 },
    { action: 'dlCode', label: 'dl.code', isDefault: false, isSelected: true, colSpan: 1 },
    { action: 'producerCode', label: 'producer.code', isDefault: false, isSelected: true, colSpan: 1 },
    { action: 'quantityStock', label: 'quantityStock', isDefault: false, isSelected: true, colSpan: 2 },
  ]

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private core: CoreService,
    public readonly scrollService: ScrollService,
    public companyUtil: CompanyUtilService,
  ) {
    this.scrollService.setKey(this.core.services.storage.keys.views.productList)
  }

  async ngOnInit() {
    this.loadViewSettings()
    this.hasRoleProductUpdate =
      this.core.services.user.hasRole('ROLE_PRODUCT_UPDATE') ||
      this.core.services.user.hasRole('ROLE_ADMIN') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
    this.hasRoleMetricsUpdate =
      this.core.services.user.hasRole('ROLE_PRODUCT_DEPOT_METRIC_UPDATE') ||
      this.core.services.user.hasRole('ROLE_ADMIN') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
    this.hasRoleMetricsCreate =
      this.core.services.user.hasRole('ROLE_PRODUCT_DEPOT_METRIC_CREATE') ||
      this.core.services.user.hasRole('ROLE_ADMIN') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
    this.hasRoleExportProductsForMetrics =
      this.core.services.user.hasRole('ROLE_EXPORT_PRODUCTS_FOR_METRICS') ||
      this.core.services.user.hasRole('ROLE_ADMIN') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
    setTimeout(async () => {
      // throttle event loop, let view load
      await this.refreshFiltersData()
      if (this.core.services.user.user) {
        this.checkPerms()
        this.purchaseCurrencyFromCompany = this.core.services.user.user.company.currency
      }
      this.core.services.user.currentUserEmitter.subscribe().then(() => {
        this.checkPerms()
        if (this.core.services.user.user) {
          this.purchaseCurrencyFromCompany = this.core.services.user.user.company.currency
        }
      })
      const settings = this.scrollService.getValue()
      if (settings?.selectedItem && settings?.currentPage) {
        const settings = this.scrollService.getValue()
        const call = async () => {
          for (const x of Array.from({ length: settings.currentPage }, (_, i) => i + 1)) {
            await this.filter(x, this.sortProp, this.sortDir, true)
          }
        }
        call()
        this.loadProducts(settings.selectedItem)
      } else {
        await this.filter()
      }

      this.keyEventSubscriptions.scannedBarcode = this.core.services.keyEvent.onScannedBarcode
        .subscribe()
        .then(code => {
          this.filterEan = code
          this.filter()
        })
    }, 100)
  }

  ngOnDestroy() {
    if (this.keyEventSubscriptions.scannedBarcode) {
      this.keyEventSubscriptions.scannedBarcode.unsubscribe()
      this.keyEventSubscriptions.scannedBarcode = null
    }
  }

  filterCategoriesChanged(categories) {
    this.filterCategories = categories
    this.filter()
  }

  hasUnsavedDataChanges(): boolean {
    return this.isEditingMetrics
  }

  getProductQuantity(product) {
    const p = this.productsWithStockAndMetrics.find(p => Number(p.productId) === Number(product.id))
    if (p?.productDepots?.length > 0) {
      return p.productDepots.reduce((total, pDepot) => {
        return pDepot.quantityStock + total
      }, 0)
    } else return 0
  }

  getProductQuantityPurchase(product, depot) {
    const p = this.productsWithStockAndMetrics.find(p => Number(p.productId) === Number(product.id))
    const quantityPurchase = p?.productDepotMetrics?.find(m => m.depot.id === depot.id)?.quantityPurchase || 0
    let byPieces = 1
    if (product?.tags?.some(t => t.externalId === 'hs-count-by-piece')) {
      byPieces = product?.packs?.find(t => t.externalId === 'hs-count-by-piece')?.quantity || 1
    }
    return quantityPurchase * byPieces
  }

  checkPerms() {
    this.canCreate =
      this.core.services.user.hasRole('ROLE_PRODUCT_CREATE') || this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
  }

  isProductRowChecked(productId: number): boolean {
    if (this.areAllProductsChecked) return true
    else {
      return this.checkedProducts.find(checkedProduct => checkedProduct.id === productId) !== undefined
    }
  }

  getProductsWithStockAndMetrics(products) {
    if (this.selectedExpiration !== 'all') this.getEndDate()

    for (const product of products) {
      this.productDepotsOfOneProduct = []
      this.productDepotMetricsOfOneProduct = []

      const depots = this.filterDepots?.length > 0 ? this.filterDepots : this.depots.map(depot => depot.id)

      this.productDepotsOfOneProduct = product.productDepots?.filter(productDepot => {
        return depots?.some(
          depotId => Number(depotId) === Number(productDepot.depot.id) && productDepot.quantityStock > 0,
        )
      })

      this.productDepotMetricsOfOneProduct = product.metrics?.filter(metric =>
        depots?.some(depotId => Number(depotId) === Number(metric.depot.id)),
      )
      this.productDepotMetricsOfOneProduct.forEach(metric => {
        const pDepots = this.productDepotsOfOneProduct?.filter(pDepot => pDepot.depot.id === metric.depot.id)
        const bla = pDepots?.reduce((total, pDepot) => {
          return pDepot.quantityStock + total
        }, 0)
        metric.isQuantityUnderMinimum = bla < metric.quantityMinimum
      })

      // filter productDepots and metrics to show if expiration is filtered
      if (this.selectedExpiration !== 'all' && this.queryFilters?.expirationDateTo?.length > 0) {
        this.productDepotsOfOneProduct = this.productDepotsOfOneProduct.filter(pDepot => {
          if (!pDepot.expirationDate) {
            return false
          }
          return new Date(pDepot.expirationDate).getTime() < this.endDate.getTime()
        })
      }

      this.productsWithStockAndMetrics.push({
        productId: product.id,
        depots: this.depots.filter(depot => {
          return (
            this.productDepotsOfOneProduct?.some(pDepot => Number(pDepot.depot.id) === Number(depot.id)) ||
            this.productDepotMetricsOfOneProduct?.some(metric => Number(metric.depot.id) === Number(depot.id))
          )
        }),
        productDepots: this.productDepotsOfOneProduct,
        productDepotMetrics: this.productDepotMetricsOfOneProduct,
      })
    }
  }

  isProductExpired(product) {
    const p = this.productsWithStockAndMetrics.find(p => Number(p.productId) === Number(product.id))
    if (!p) {
      return false
    }
    const newDate = new Date()
    const todayDate = newDate.getTime()
    return p.productDepots?.some(productDepot => {
      if (productDepot.expirationDate?.length > 0) {
        const expDate = new Date(productDepot.expirationDate)
        return expDate.getTime() < todayDate
      } else {
        return false
      }
    })
  }

  isProductDepotExpired(pDepot) {
    const newDate = new Date()
    const todayDate = newDate.getTime()
    if (pDepot.expirationDate?.length > 0) {
      const expDate = new Date(pDepot.expirationDate)
      return expDate.getTime() < todayDate
    } else {
      return false
    }
  }

  isProductQuantityUnderMinimum(productId: number) {
    const p = this.productsWithStockAndMetrics.find(p => Number(p.productId) === Number(productId))
    if (p?.depots?.length > 0 && p?.productDepotMetrics?.length > 0) {
      return p.productDepotMetrics.some(metric => {
        const depot = p.depots.find(d => d.id === metric.depot.id)
        return metric.quantityMinimum > this.getDepotQuantityStock(productId, depot.id)
      })
    } else return false
  }

  isDepotQuantityUnderMinimum(productId: number, depotId?: number) {
    const p = this.productsWithStockAndMetrics.find(p => Number(p.productId) === Number(productId))
    if (depotId && p?.productDepotMetrics?.length > 0) {
      const pDepotMetricOfThisDepot = p?.productDepotMetrics.find(pDepotMetric => pDepotMetric.depot.id === depotId)
      const ble = this.getDepotQuantityStock(productId, depotId)
      return pDepotMetricOfThisDepot?.quantityMinimum > ble
    } else return false
  }

  getDepotQuantityStock(productId: number, depotId: number) {
    const prod = this.products.find(p => p.id === productId)
    if (prod) {
      const pDepotsOfThisDepot = prod.productDepots.filter(pDepot => pDepot.depot.id === depotId)
      return pDepotsOfThisDepot.reduce((total, pDepot) => {
        return total + pDepot.quantityStock
      }, 0)
    }
    return 0
  }

  stockRowRolled(productId: number) {
    if (this.rolledStockRowes.some(prod => prod === productId)) {
      this.rolledStockRowes = this.rolledStockRowes.filter(prod => prod !== productId)
      this.areAllStockRowsVisible = false
    } else {
      this.rolledStockRowes.push(productId)
    }
  }

  isStockRowRolled(productId: number): boolean {
    return this.rolledStockRowes.some(prod => prod === productId)
  }

  getQuantityMinimum(product, depot): number {
    const metric = product.metrics.find(metric => metric.depot.id === depot.id)
    if (metric?.id) {
      return metric?.quantityMinimum ? metric.quantityMinimum : 0
    } else {
      return 0
    }
  }

  async getProductDepotMetric(product, depot): Promise<ProductDepotMetric> {
    let result
    let productDepotMetric
    const filters = { product: product.id, depot: depot.id }
    try {
      result = await this.core.services.productDepotMetric.getList(
        { page: 1, sort: 'id', direction: null, filters: filters },
        SchemaUtil.productDepotMetric,
      )
    } catch (err) {
      console.log(err)
      return
    }
    if (result?.items?.length > 0) {
      productDepotMetric = result.items.find(object => {
        return object.depot?.id === depot.id
      })
      if (productDepotMetric?.id) {
        productDepotMetric = new ProductDepotMetric(productDepotMetric)
      } else {
        productDepotMetric = new ProductDepotMetric({ product: { id: product.id }, depot: { id: depot.id } })
      }
    } else {
      productDepotMetric = new ProductDepotMetric({ product: { id: product.id }, depot: { id: depot.id } })
    }
    return productDepotMetric
  }

  fetchProductDepotMetric(product, depot): ProductDepotMetric {
    return product.metrics.find(metric => metric.depot.id === depot.id)
  }

  getQuantityOptimum(product, depot): number {
    const metric = product.metrics.find(metric => metric.depot.id === depot.id)
    if (metric?.id) {
      return metric?.quantityOptimum ? metric.quantityOptimum : 0
    } else {
      return 0
    }
  }

  productChecked(checkboxValue: boolean, product: Product): void {
    if (checkboxValue) {
      this.checkedProducts.push(product)
    } else {
      this.areAllProductsChecked = false
      this.checkedProducts = this.checkedProducts.filter(checkedProduct => checkedProduct.id != product.id)
    }
  }

  allProductsChecked(event: boolean) {
    if (event) {
      this.areAllProductsChecked = true
      this.checkedProducts = this.products
    } else {
      this.areAllProductsChecked = false
      this.checkedProducts = []
    }
  }

  async refreshFiltersData() {
    this.depots = this.core.lists.depots.filter(depot => {
      return depot.name !== 'all' && this.core.services.user?.user?.depots?.some(d => d.id === depot.id)
    })
    this.suppliers = [{ id: 0, name: 'all.suppliers', externalId: '' }, ...this.core.lists.suppliers]
    this.rolledStockRowes = []
    this.productDepotMetrics = []
    this.productsWithStockAndMetrics = []
    this.isEditingMetrics = false
    this.onFilterDepotsChange(this.filterDepots)
  }

  get queryFilters() {
    const f = {
      // listType: this.filterListType,
      listType: 'children_and_parents_without_children', // we only want this one
      availability: this.selectedAvailability,
      isAutomaticOrders: this.automaticOrderingType === 'yes' ? true : this.filterEnabled === 'no' ? false : undefined,
      enabled: this.filterEnabled === 'yes' ? true : this.filterEnabled === 'no' ? false : undefined,
      metrics: this.filterMetrics,
      fulltext: this.filterFulltext && this.filterFulltext.length ? this.filterFulltext : undefined,
      name: this.filterName && this.filterName.length ? this.filterName : undefined,
      ean:
        this.filterEan && this.filterEan.length && this.filterFulltext && this.filterFulltext.length
          ? this.filterEan
          : undefined,
      code: this.filterCode && this.filterCode.length ? this.filterCode : undefined,
      expirationDateTo:
        this.selectedExpirationDate && this.selectedExpirationDate?.length
          ? this.selectedExpirationDate.trim()
          : undefined,
      depots: this.filterDepots || [],
      producers: this.filterProducers || [],
      suppliers: this.filterSuppliers || [],
      tags: this.filterTags || [],
      categories: this.filterCategories?.map(category => category?.id) || [],
    }
    Object.keys(f).forEach(k => {
      if (f[k] === undefined) {
        delete f[k]
      }
    })
    return f
  }

  onFilterDepotsChange(event: number[]) {
    this.filterDepots = event
  }

  async filter(
    pageNo = 1,
    sortProp: string = this.sortProp,
    sortDir: string = this.sortDir,
    concat = false,
  ): Promise<any> {
    this.loading = true
    if (!concat) this.reloading = true
    if (this.products?.length < 1) this.productsWithStockAndMetrics = []
    this.currentPage = pageNo

    this.areAllStockRowsVisible =
      this.queryFilters.depots?.length > 0 || this.queryFilters.expirationDateTo?.length > 0 || this.isEditingMetrics
    const filters = this.queryFilters
    if (filters.depots?.length < 1) filters.depots = this.depots.map(d => d.id)
    const result = await this.core.services.product.getList(
      { page: pageNo, sort: sortProp, direction: sortDir, filters: this.queryFilters },
      SchemaUtil.productList,
    )
    try {
      if (result?.items) await this.getProductsWithStockAndMetrics(result.items)
    } catch (error) {
      console.warn('products.getProductsWithStockAndMetrics error', { error })
    } finally {
      if (!concat) {
        this.products =
          result?.items?.map(p => new Product({ ...p, purchaseCurrency: this.purchaseCurrencyFromCompany })) || []
      } else {
        result?.items?.forEach(product => {
          this.products.push(new Product({ ...product, purchaseCurrency: this.purchaseCurrencyFromCompany }))
        })
      }
      // we're not gonna need those, so .. we're not gonna save those, at least for now:
      // this.scrollService.save('products', this.products)
      // this.scrollService.save('productsWithStockAndMetrics', this.productsWithStockAndMetrics)
      this.scrollService.save('currentPage', this.currentPage)
    }
    this.reloading = false
    this.totalItems = result?.paginator ? result?.paginator?.totalCount : 0
    this.endPage = result?.paginator ? result?.paginator?.endPage : 0
    this.scrollService.save('endPage', this.endPage)
    this.scrollService.save('totalItems', this.totalItems)
    this.saveViewSettings()
    setTimeout(() => (this.loading = false), 1250)
  }

  editDepotMetrics() {
    this.isEditingMetrics = true
    this.productDepotMetrics = []
    this.areAllStockRowsVisible = true
  }

  async saveDepotMetrics() {
    for (const metric of this.productDepotMetrics) {
      try {
        if (metric?.id > 0) {
          await this.core.services.productDepotMetric.update(metric, SchemaUtil.productDepotMetric)
        } else {
          await this.core.services.productDepotMetric.create(metric, SchemaUtil.productDepotMetric)
        }
      } catch (err) {
        console.log(err)
      }
    }
    this.isEditingMetrics = false
    this.metricsLoading = false
    this.productDepotMetrics = []
    this.areAllStockRowsVisible = false
    this.productsWithStockAndMetrics = []
    await this.filter()
  }

  async quantityMinimumChanged(event, product, depot) {
    if (this.productDepotMetrics.some(metric => metric.product?.id === product.id && metric.depot?.id === depot.id)) {
      // check if it's already in productDepotMetrics array
      const filteredMetricInArray = this.productDepotMetrics.filter(
        metric => metric.product?.id === product.id && metric.depot?.id === depot.id,
      )
      filteredMetricInArray[0].quantityMinimum = event
    } else {
      // check if metric already exists
      let metric = product.metrics.find(metric => metric.depot.id === depot.id)
      if (metric?.id) {
        metric.quantityMinimum = event
      } else {
        metric = new ProductDepotMetric({
          product: { id: product.id },
          depot: { id: depot.id },
          quantityMinimum: event,
        })
      }
      this.productDepotMetrics.push(metric)
    }
  }

  cancelEditingMetricsMode() {
    this.isEditingMetrics = false
    this.areAllStockRowsVisible = false
    this.productDepotMetrics = []
  }

  async quantityOptimumChanged(event, product, depot) {
    if (this.productDepotMetrics.some(metric => metric.product?.id === product.id && metric.depot?.id === depot.id)) {
      // check if it's already in productDepotMetrics array
      const filteredMetricInArray = this.productDepotMetrics.filter(
        metric => metric.product?.id === product.id && metric.depot?.id === depot.id,
      )
      filteredMetricInArray[0].quantityOptimum = event
    } else {
      // check if metric already exists
      let metric = product.metrics.find(metric => metric.depot.id === depot.id)
      if (metric?.id) {
        metric.quantityOptimum = event
      } else {
        metric = new ProductDepotMetric({
          product: { id: product.id },
          depot: { id: depot.id },
          quantityOptimum: event,
        })
      }
      this.productDepotMetrics.push(metric)
    }
  }

  productDepotMetricChanged(event) {
    if (
      this.productDepotMetrics.some(
        metric => metric.product?.id === event?.product?.id && metric.depot?.id === event?.depot?.id,
      )
    )
      return
    else this.productDepotMetrics.push(event)
  }

  public clearFilters() {
    this.filterFulltext = ''
    this.filterDepots = []
    this.filterCategories = []
    this.filterTags = []
    this.filterSuppliers = []
    this.filterProducers = []
    this.filterListType = 'children_and_parents_without_children'
    this.selectedAvailability = 'all'
    this.filterEnabled = 'yes'
    this.filterMetrics = ''
    this.selectedExpiration = null
    this.selectedExpirationDate = null
    this.filterName = ''
    this.filterCode = ''
    this.filterEan = ''
    this.productsWithStockAndMetrics = []
    this.filter()
    this.saveViewSettings()
  }

  sortBy(property: string): void {
    this.sortProp === property
      ? this.sortDir === 'asc'
        ? (this.sortDir = 'desc')
        : (this.sortDir = 'asc')
      : (this.sortProp = property)
    this.currentPage = 1
    this.filter(this.currentPage, this.sortProp, this.sortDir, false)
    this.saveViewSettings()
  }

  scrolled(overrideScroll = false) {
    const el = document?.querySelector('#btn-paginate')
    if ((el && !this.loading) || overrideScroll) {
      if ((!this.currentPage || this.currentPage <= this.endPage) && this.totalItems > this.products?.length) {
        if (!this.currentPage) {
          this.currentPage = 1
        }
        this.currentPage++
        this.filter(this.currentPage, this.sortProp, this.sortDir, true)
      }
    }
  }

  setExpirationFilter(expStr: string = null, expDate: string = null) {
    if (expStr) {
      this.selectedExpiration = expStr
      let date = new Date()
      switch (this.selectedExpiration) {
        case 'all':
          this.selectedExpirationDate = null
          return
        case 'monthEnd':
          date = new Date(date.getFullYear(), date.getMonth() + 1, 0)
          break
        case 'after1month':
          date.setMonth(date.getMonth() + 1)
          break
        case 'after3months':
          date.setMonth(date.getMonth() + 3)
          break
        case 'custom':
          return
      }

      const tzOffset = date.getTimezoneOffset()
      date.setTime(date.getTime() + tzOffset * 60 * -1000)

      this.selectedExpirationDate = `${date.toISOString().substring(0, 10)} 23:59:59`
    } else if (expDate) {
      this.selectedExpirationDate = `${new Date(expDate).toISOString().substring(0, 10)} 23:59:59`
    }
  }

  saveViewSettings(): void {
    const settings = {
      filterVisible: this.filterVisible,
      filterListType: this.filterListType,
      filterEnabled: this.filterEnabled,
      filterMetrics: this.filterMetrics,
      filterFulltext: this.filterFulltext,
      filterDepots: this.filterDepots,
      filterCategories: this.filterCategories,
      filterTags: this.filterTags,
      filterProducers: this.filterProducers,
      filterSuppliers: this.filterSuppliers,
      selectedAvailability: this.selectedAvailability,
      filterRowsShown: this.filterRowsShown,
      filterName: this.filterName,
      filterCode: this.filterCode,
      // filterEan: this.filterEan,
      sortProp: this.sortProp,
      sortDir: this.sortDir,
      selectedColumns: this.selectedColumns,
    }
    this.core.services.storage.set(this.core.services.storage.keys.views.products, settings)
  }

  private loadProducts(selectedItem): void {
    this.scrollService.scrollTo(selectedItem)
    // this.loading = false
    // this.reloading = false
    // const settings = this.scrollService.getValue()
    // Object.keys(settings)?.forEach(value => (this[value] = settings[value]))
    // this.scrollService.scrollTo(settings.selectedItem)
  }

  loadViewSettings(): void {
    const settings = this.core.services.storage.getSync(this.core.services.storage.keys.views.products)
    if (settings && Object.keys(settings)?.length > 0) {
      this.filterVisible = settings.filterVisible
      this.filterListType = settings.filterListType || 'children_and_parents_without_children'
      this.filterEnabled = settings.filterEnabled || 'yes'
      this.filterMetrics = settings.filterMetrics || ''
      this.filterFulltext = settings.filterFulltext
      this.filterDepots = settings.filterDepots || []
      this.filterCategories = settings.filterCategories || []
      this.filterTags = settings.filterTags || []
      this.filterProducers = settings.filterProducers || []
      this.filterSuppliers = settings.filterSuppliers || []
      this.selectedAvailability = settings.selectedAvailability || 'all'
      this.filterName = settings.filterName
      this.filterCode = settings.filterCode
      // this.filterEan = settings.filterEan
      this.sortProp = settings.sortProp
      this.sortDir = settings.sortDir
      if (settings.selectedColumns?.length && settings.selectedColumns[0]?.colSpan) {
        this.selectedColumns = settings.selectedColumns
      }
    }
    this.arePositionsEnabled = this.companyUtil.arePositionsEnabled
  }

  setCheckedProductsToFalse() {
    this.areAllProductsChecked = false
    this.checkedProducts = []
  }

  canShowStockBtn(productId: number) {
    const p = this.productsWithStockAndMetrics.find(p => Number(p.productId) === Number(productId))
    return p?.productDepotMetrics?.length > 0 || p?.productDepots?.length > 0
  }

  getDepotsOfProduct(productId: number) {
    if (this.isEditingMetrics) {
      return this.depots
    } else {
      const p = this.getProductWithStockAndMetrics(productId)
      return p?.depots
    }
  }

  hasDepotPDepots(productId: number, depot: Depot): boolean {
    const p = this.getProductWithStockAndMetrics(productId)
    const pDepots = p?.productDepots?.filter(pDepot => pDepot.depot.id === depot.id)
    return pDepots?.length > 0
  }

  getPDepotsOnDepot(productId: number, depot: Depot): ProductDepot[] {
    const p = this.getProductWithStockAndMetrics(productId)
    return p?.productDepots?.filter(pDepot => pDepot.depot.id === depot.id) || []
  }

  getProductWithStockAndMetrics(productId: number) {
    return this.productsWithStockAndMetrics.find(p => p.productId === productId)
  }

  getThumbUrl(image: File, format: 'w135wp' | 'w2048wp' = 'w135wp'): string {
    return imageGetThumbUrl(image, format)
  }

  getEndDate() {
    const currentDate = new Date()
    let endDate
    if (this.selectedExpiration === 'monthEnd') {
      endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
    } else if (this.selectedExpiration === 'after1month') {
      endDate = new Date(new Date().setMonth(currentDate.getMonth() + 1))
    } else if (this.selectedExpiration === 'after3months') {
      endDate = new Date(new Date().setMonth(currentDate.getMonth() + 3))
    } else if (this.selectedExpiration === 'custom') {
      endDate = new Date(this.selectedExpirationDate)
    }
    this.endDate = endDate
  }

  onColumnSettingDropdownClick(event: string) {
    const col = this.selectedColumns.find(c => c.action === event)
    const isSelected = col.isSelected
    for (const c of this.selectedColumns) {
      if (c.action === event) {
        c.isSelected = !isSelected
      }
    }
    this.saveViewSettings()
  }

  isColumnSelected(property: string) {
    return this.selectedColumns.find(c => c.action === property)?.isSelected
  }

  get stockRowColSpan(): number {
    const colspan = this.selectedColumns
      .filter(c => c.isSelected)
      .map(c => c.colSpan)
      .reduce((a, b) => a + b, 1)
    return colspan + 4 // add default columns
  }
}
